// src/components/Entregas/ProductosPOSeriales.jsx
import React, { useEffect, useState } from 'react';
import {
    Modal,
    Form,
    Input,
    Spin,
    message,
    Divider,
    Button,
    Popconfirm
} from 'antd';
import {
    SaveOutlined,
    BarcodeOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import {
    getSerialesProducto,
    registrarSerialesProducto
} from '../../services/entregas/servicesOrdenesCompra';

const ProductosPOSeriales = ({ visible, record, onClose, onSave }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [initialSeriales, setInitialSeriales] = useState(null);

    // 1) Al abrir el modal calculamos initialSeriales
    // 1) Al abrir el modal calculamos initialSeriales
    useEffect(() => {
        if (!visible || !record) {
            setInitialSeriales(null);
            return;
        }

        const target = Math.max(0, Number(record.cantidad) || 0);
        const makePlaceholders = (n) => Array.from({ length: n }, () => ({ serial: '', nota: '' }));

        const cargar = async () => {
            setLoading(true);
            try {
                const oidProd = Number(record?.oidInsertado);
                let existentes = [];

                if (Number.isFinite(oidProd) && oidProd > 0) {
                    const resp = await getSerialesProducto(oidProd);
                    if (resp.status === 200 && Array.isArray(resp.data)) {
                        // normaliza por si vienen campos extra
                        existentes = resp.data.map(s => ({
                            oid: s.oid,                       // si viene
                            serial: s.serial ?? '',
                            nota: s.nota ?? '',
                        }));
                    }
                }

                // merge según target
                if (existentes.length === 0) {
                    setInitialSeriales(makePlaceholders(target));
                } else if (existentes.length < target) {
                    setInitialSeriales([
                        ...existentes,
                        ...makePlaceholders(target - existentes.length),
                    ]);
                } else if (existentes.length > target) {
                    // opcional: avisa que se recorta
                    message.warning(
                        `Hay ${existentes.length} serial(es) registrado(s) pero la cantidad es ${target}. Se mostrarán los primeros ${target}.`
                    );
                    setInitialSeriales(existentes.slice(0, target));
                } else {
                    setInitialSeriales(existentes);
                }
            } catch (e) {
                // si falla la carga, al menos crea placeholders
                setInitialSeriales(makePlaceholders(target));
            } finally {
                setLoading(false);
            }
        };

        cargar();
        form.resetFields();
        // 👇 importante: dependemos explícitamente de la cantidad
    }, [visible, record, form, record?.cantidad]);

    // 2) Cuando initialSeriales esté listo, volcamos al formulario
    useEffect(() => {
        if (initialSeriales !== null) {
            form.setFieldsValue({ seriales: initialSeriales });
        }
    }, [initialSeriales, form]);



    const handleOk = async () => {
        try {
            // 1) Valida el form
            const values = await form.validateFields();

            // 2) Normaliza (trim) antes de enviar
            const payload = (values.seriales || []).map((s) => ({
                serial: String(s?.serial ?? '').trim(),
                nota: s?.nota ?? '',
            }));


            // 3) Llama al servicio (asegúrate que envía { seriales: payload })
            const resp = await registrarSerialesProducto(record.oidInsertado, payload);

            if (resp.status === 200 || resp.status === 201 || resp?.data?.status === 201) {
                message.success(resp?.data?.message || 'Seriales guardados correctamente');
                onSave(payload);
                onClose();
            } else {
                message.error(resp?.data?.message || 'Error al guardar seriales');
            }
        } catch (err) {
            // A) Error de validación AntD
            if (err?.errorFields) {
                message.error('Por favor completa todos los seriales requeridos');
                return;
            }

            // B) Errores devueltos por el backend
            const status = err?.response?.status;
            const code = err?.response?.data?.code;
            let msgFromServer = err?.response?.data?.message;
            let duplicados = err?.response?.data?.data?.duplicados || [];

            // Fallback: si el backend no envía la lista, la detectamos en el formulario
            const current = (form.getFieldsValue()?.seriales || []).map((s) => String(s?.serial ?? '').trim());
            if ((status === 400 && code === 'SERIAL_REPETIDO_EN_PAYLOAD') && duplicados.length === 0) {
                const seen = new Map();
                const rep = [];
                current.forEach((s) => {
                    const c = (seen.get(s) || 0) + 1;
                    seen.set(s, c);
                    if (c === 2) rep.push(s); // solo una vez por duplicado
                });
                duplicados = rep;
            }

            // Marca duplicados o vacíos en el form
            if (
                (status === 409 && code === 'SERIAL_DUPLICADO') ||
                (status === 400 && code === 'SERIAL_REPETIDO_EN_PAYLOAD') ||
                (status === 400 && code === 'SERIAL_VACIO')
            ) {
                const dupSet = new Set(duplicados.map((d) => String(d ?? '').trim()));
                const fieldErrors = current.map((s, idx) => {
                    if (code === 'SERIAL_VACIO' && s.length === 0) {
                        return { name: ['seriales', idx, 'serial'], errors: ['Obligatorio'] };
                    }
                    if ((code === 'SERIAL_REPETIDO_EN_PAYLOAD' || code === 'SERIAL_DUPLICADO') && dupSet.has(s)) {
                        return { name: ['seriales', idx, 'serial'], errors: ['Serial duplicado'] };
                    }
                    return null;
                }).filter(Boolean);

                if (fieldErrors.length) form.setFields(fieldErrors);
                message.warning(msgFromServer || 'Hay errores en los seriales.');
                return;
            }

            // C) Otros errores
            console.error('[guardar seriales] error:', err);
            message.error(msgFromServer || err?.message || 'No se pudo guardar los seriales');
        }
    };


    const confirmSalir = () => {
        Modal.confirm({
            title: '¿Quieres salir del registro?',
            okText: 'Sí',
            cancelText: 'No',
            onOk: onClose,
        });
    };

    return (
        <Modal
            open={visible}
            title={record ? `Seriales para: "${record.producto}"` : 'Seriales'}
            onCancel={confirmSalir}
            maskClosable
            destroyOnClose={false}
            footer={[
                <React.Fragment key="footer-buttons">
                    <Popconfirm
                        title="¿Quieres salir del registro?"
                        onConfirm={onClose}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button key="cancel">
                            <CloseCircleOutlined style={{ marginRight: 8 }} />
                            Cancelar
                        </Button>
                    </Popconfirm>
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleOk}
                        disabled={loading || initialSeriales === null}
                    >
                        <SaveOutlined style={{ marginRight: 8 }} />
                        Guardar seriales
                    </Button>
                </React.Fragment>
            ]}
            width="70%"
            style={{ top: 20, maxHeight: '90vh' }}
        >
            <Spin spinning={loading || initialSeriales === null}>
                <Form form={form} layout="vertical" name="form_seriales">
                    <Form.List name="seriales">
                        {fields => (
                            <>
                                {fields.map((field, index) => {
                                    // Desestructuramos para quitar key de {...restField}
                                    const { key, name, ...restField } = field;
                                    return (
                                        <React.Fragment key={`${key}-${name}`}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 16,
                                                    marginBottom: 0,
                                                }}
                                            >
                                                {/* Índice y icono */}
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: 48,
                                                        justifyContent: 'center',
                                                        paddingTop: 8,
                                                    }}
                                                >
                                                    <span style={{ marginRight: 6 }}>{index + 1}</span>
                                                    <BarcodeOutlined style={{ fontSize: 20, color: '#0d6efd' }} />
                                                </div>

                                                {/* Serial */}
                                                <Form.Item
                                                    name={[name, 'serial']}
                                                    style={{ flex: 1 }}
                                                >
                                                    <Input.TextArea
                                                        rows={1}
                                                        placeholder="Ingrese serial"
                                                        style={{ resize: 'both', overflow: 'auto' }}
                                                    />
                                                </Form.Item>

                                                {/* Nota */}
                                                <Form.Item
                                                    name={[name, 'nota']}
                                                    style={{ flex: 1 }}
                                                >
                                                    <Input.TextArea
                                                        rows={1}
                                                        placeholder="Ingrese nota"
                                                        style={{ resize: 'both', overflow: 'auto' }}
                                                    />
                                                </Form.Item>
                                            </div>
                                            {/* Separador */}
                                            {index < fields.length - 1 && (
                                                <Divider style={{ margin: 5 }} />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </>
                        )}
                    </Form.List>
                </Form>
            </Spin>
        </Modal>
    );
};

export default ProductosPOSeriales;
